// mirror of auware/.../frontend/src/pages/auth/ForgotPasswordPage.tsx (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations: brand strings + logo swapped.
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import Button from "@/components/ui/Button";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

/**
 * Email input for password reset. Always shows the same success message
 * regardless of whether the email exists (prevents account enumeration).
 */
export function ForgotPasswordPage(): JSX.Element {
  usePageTitle("Forgot password");
  const [email, setEmail] = useState("");
  // SPEC §15 honeypot. Real users never fill this; bots auto-fill any
  // input.
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/auth/forgot-password", { email, website });
    } catch {
      // Swallow - always show "check your email" to prevent enumeration
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <h2 className="mt-2 text-xl font-semibold text-neutral-900">
            Reset your password
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        <div className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
          {sent ? (
            <>
              <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                If an account with that email exists, a password reset link has
                been sent. Check your inbox.
              </div>
              <Link
                to="/login"
                className="block w-full text-center text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Back to sign in
              </Link>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="reset-email"
                  className="block text-sm font-medium text-neutral-700"
                >
                  Email address
                </label>
                <input
                  id="reset-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="you@company.com"
                  autoFocus
                />
              </div>

              {/* Honeypot - hidden from real users, autofilled by bots. */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label htmlFor="forgot-website">Website</label>
                <input
                  id="forgot-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <Button type="submit" className="w-full" isLoading={loading}>
                Send reset link
              </Button>

              <Link
                to="/login"
                className="block w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
